import React, { useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetTask } from 'selectors/tasks';

import { updateTask, linkTasks, unlinkTasks } from 'actions/tasks';

import { OverlayTaskContext } from 'context/OverlayTaskContext';

import TaskContainer from 'containers/task-container';

import SelectTaskPopout from 'components/select-task-popout';
import TaskNiceId from 'components/TaskNiceId';
import TaskCompleteToggle from 'components/TaskCompleteToggle';
import TaskCompleteWrap from 'components/TaskCompleteWrap';
import CanUser from 'components/can-user';
import Tooltip from 'components/tooltip';

import IconLink from 'icons/icon-link';
import IconBrokenLink from 'icons/icon-broken-link';

import './LinkedTasks.styles.css';

const LinkedTaskEntry = ({ id, name, nice_id, completed, parentTaskCreatedBy, onClick, onUnlinkClick }) => {
    const { t } = useTranslation('task');

    const handleClick = () => {
        onClick(id);
    }

    const handleUnlinkClick = () => {
        onUnlinkClick(id);
    }

    const handleCompleteToggle = (completed, taskGroupId) => {
        updateTask(id, {
            completed,
            completed_on: new Date(),
            task_group: taskGroupId ? taskGroupId : undefined
        });
    }

    if (!name) {
        return null;
    }

    return (
        <div className="linked-task-entry">
            <div className="entry-head">
                <TaskCompleteWrap id={id} dark={true} onChange={handleCompleteToggle} />

                <TaskNiceId niceId={nice_id} />
            </div>

            <div className="entry-body" onClick={handleClick}>
                { name }
            </div>

            <CanUser type="editTask" customList={parentTaskCreatedBy}>
                <div className="entry-foot">
                    <Tooltip tooltip={ t('actions.unlinkTask.title') } withTooltipHover={false} placement="top">
                        <button className="btn btn-circle btn-xxsmall btn-transparent btn-icon" onClick={handleUnlinkClick}>
                            <IconBrokenLink />
                        </button>
                    </Tooltip>
                </div>
            </CanUser>
        </div>
    );
}

const LinkedTasks = ({ taskId, projectId, linkedTasks, taskCreatedBy }) => {
    const { t } = useTranslation('task');

    const overlayTask = useContext(OverlayTaskContext);

    const handleLinkTaskSelect = useCallback(otherTaskId => {
        linkTasks(taskId, [otherTaskId]);
    }, [taskId]);

    const handleTaskClick = useCallback(otherTaskId => {
        overlayTask?.openTask(otherTaskId);
    }, [overlayTask?.openTask]);

    const handleTaskUnlinkClick = useCallback(otherTaskId => {
        unlinkTasks(taskId, [otherTaskId]);
    }, [taskId]);

    return (
        <div className="content-panel-block">
            <div className="content-panel-section">
                <div className="section-head">
                    <h4>
                        <span className="title-icon"><IconLink /></span>

                        <span className="text">{ t('Linked Tasks') }</span>

                        <CanUser type="editTask" customList={taskCreatedBy}>
                            <SelectTaskPopout
                                title={ t('actions.linkTask.title') }
                                excluded={[taskId]}
                                projectId={projectId}
                                xAlign="right"
                                defaultXPosition="left"
                                onSelect={handleLinkTaskSelect}
                            >
                                <button className="btn btn-circle btn-xxsmall btn-simple btn-icon btn-colored">
                                    +
                                </button>
                            </SelectTaskPopout>
                        </CanUser>
                    </h4>
                </div>

                <div className="section-body">
                    {
                        linkedTasks.map(taskId => (
                            <TaskContainer key={taskId} id={taskId}>
                                <LinkedTaskEntry
                                    parentTaskCreatedBy={taskCreatedBy}
                                    onClick={handleTaskClick}
                                    onUnlinkClick={handleTaskUnlinkClick}
                                />
                            </TaskContainer>
                        ))
                    }
                </div>
            </div>
        </div>
    );
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();

    return ({ tasks }, ownProps) => {
        const task = getTask(tasks.items, ownProps.taskId);

        return {
            projectId: task?.project,
            linkedTasks: task?.linked_tasks,
            taskCreatedBy: task?.created_by
        }
    };
}

export default connect(makeMapStateToProps)(LinkedTasks);
